import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  HiOutlineBolt,
  HiOutlineSparkles,
  HiOutlineCircleStack,
  HiOutlineInboxStack,
  HiOutlinePlusCircle,
  HiOutlineShoppingCart,
  HiOutlineXMark,
  HiOutlineDocumentText,
  HiOutlineChatBubbleLeftRight,
  HiOutlinePaperAirplane,
  HiOutlineWrenchScrewdriver,
  HiOutlinePlay,
  HiOutlineGift,
  HiOutlineHeart,
  HiOutlineUserCircle,
} from "react-icons/hi2";
import { Modal } from "antd";
import { usersApi, billingApi } from "../../api";
import { CreditPack, TeamMemberUsage, UsageHistoryRecord } from "../../api/billing";
import { useUser } from "../../hooks";
import { ByokKeyAlert } from "../byok-key";
import { notify } from "../../util/notify/notify";
import { storeTeamActions } from "../../store/slices/team";
import { storeAuthActions } from "../../store/slices/auth";
import { Avatar, Panel, FormButton } from "../../ui";
import "../../ui/simple-table";
import { DataTablePagination } from "../../ui/data-table/data-table-pagination/data-table-pagination";
import moment from "moment";
import "./connects.scss";

// ── Action display config ──

const ACTION_CONFIG: Record<string, { label: string; icon: JSX.Element; color: string }> = {
  ai_proposal: { label: "Cover Letter", icon: <HiOutlineDocumentText size={14} />, color: "#22c55e" },
  ai_highlights: { label: "Highlights", icon: <HiOutlineSparkles size={14} />, color: "#22c55e" },
  // legacy action kept for historical rows
  ai_proposal_highlights: { label: "Cover Letter + Highlights", icon: <HiOutlineDocumentText size={14} />, color: "#22c55e" },
  ai_answer: { label: "Answer", icon: <HiOutlineChatBubbleLeftRight size={14} />, color: "#60a5fa" },
  ai_job_assistant: { label: "Job Assistant", icon: <HiOutlineSparkles size={14} />, color: "#a78bfa" },
  ai_profile_analysis: { label: "Profile Analysis", icon: <HiOutlineUserCircle size={14} />, color: "#a78bfa" },
  ai_profile_fix: { label: "Profile Fix", icon: <HiOutlineWrenchScrewdriver size={14} />, color: "#a78bfa" },
  auto_bid: { label: "Auto Bid", icon: <HiOutlinePaperAirplane size={14} />, color: "#fb923c" },
  auto_bid_match: { label: "Job Matching", icon: <HiOutlinePaperAirplane size={14} />, color: "#fb923c" },
  ai_improve_template: { label: "AI Improve", icon: <HiOutlineWrenchScrewdriver size={14} />, color: "#f472b6" },
  ai_generate_template: { label: "AI Generate", icon: <HiOutlineSparkles size={14} />, color: "#f472b6" },
  ai_test_template: { label: "Test Template", icon: <HiOutlinePlay size={14} />, color: "#38bdf8" },
  comeback_bonus: { label: "Comeback Bonus", icon: <HiOutlineGift size={14} />, color: "#22c55e" },
  winback_bonus: { label: "Welcome-Back Bonus", icon: <HiOutlineHeart size={14} />, color: "#22c55e" },
  support_bonus: { label: "Support Bonus", icon: <HiOutlineGift size={14} />, color: "#22c55e" },
  // Unused legacy-plan credits moved to the extra balance on the switch to Flex
  plan_migration_carryover: { label: "Credits carried over", icon: <HiOutlineGift size={14} />, color: "#22c55e" },
};

// Actions that GRANT credits (rendered as +N green) instead of consuming (-N).
const GRANT_ACTIONS = new Set(["comeback_bonus", "winback_bonus", "support_bonus", "plan_migration_carryover"]);


// ── Component ──

export const Connects = (): JSX.Element => {
  const dispatch = useDispatch();
  const { currentUser } = useUser();
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [expandedMembers, setExpandedMembers] = useState<Set<number>>(new Set([0]));
  const [expandedUsages, setExpandedUsages] = useState<Set<number>>(new Set([0]));

  const toggleInSet = (
    setter: React.Dispatch<React.SetStateAction<Set<number>>>,
    index: number
  ): void => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  // Team usage state
  const [teamUsage, setTeamUsage] = useState<TeamMemberUsage[]>([]);

  // Usage history state
  const [usageHistory, setUsageHistory] = useState<UsageHistoryRecord[]>([]);
  const [usageTotal, setUsageTotal] = useState(0);
  const [usageLoading, setUsageLoading] = useState(true);

  // Purchase state
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [creditPacks, setCreditPacks] = useState<CreditPack[]>([]);
  const [selectedPackIndex, setSelectedPackIndex] = useState(0);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [packsLoading, setPacksLoading] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  const canPurchase = currentUser?.role === "owner" || currentUser?.role === "admin";
  const hasSubscription = currentUser?.has_active_subscription;

  // ── Fetch data on mount ──

  const fetchUsageHistory = useCallback(async (page: number, pp: number) => {
    setUsageLoading(true);
    try {
      const res = await billingApi.getUsageHistory(page, pp);
      setUsageHistory(res.data);
      setUsageTotal(res.total);
      setCurrentPage(res.current_page);
    } catch {
      // silently fail
    } finally {
      setUsageLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [membersRes, usageRes] = await Promise.all([
          usersApi.getTeamMembers(),
          billingApi.getTeamUsage(),
        ]);
        if (!mounted) return;
        dispatch(storeTeamActions.setMembers(membersRes.data));
        setTeamUsage(usageRes.members);
      } catch {
        // silently fail — data may already be in Redux
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    fetchData();
    fetchUsageHistory(1, perPage);
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auto-open modal from ?topup=1 ──

  useEffect(() => {
    if (searchParams.get("topup") === "1" && canPurchase && hasSubscription && !isLoading) {
      openPurchaseModal();
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, canPurchase, hasSubscription]);

  // ── Load credit packs when modal opens ──

  const openPurchaseModal = useCallback(async () => {
    setShowPurchaseModal(true);
    if (creditPacks.length > 0) return;

    setPacksLoading(true);
    try {
      const res = await billingApi.getCreditPacks();
      setCreditPacks(res.packs);
      setSelectedPackIndex(0);
    } catch {
      notify("error", "Failed to load credit packs.");
    } finally {
      setPacksLoading(false);
    }
  }, [creditPacks.length]);

  // ── Purchase handler ──

  const handlePurchase = useCallback(async () => {
    const pack = creditPacks[selectedPackIndex];
    if (!pack) return;

    setIsPurchasing(true);
    try {
      const res = await billingApi.purchaseCredits(pack.credits);
      notify("success", res.message);

      // Refresh user data to update credits
      const userRes = await usersApi.getMe();
      dispatch(storeAuthActions.setUserData({ user: userRes.data }));

      setShowPurchaseModal(false);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || "Purchase failed. Please try again.";
      notify("error", msg);
    } finally {
      setIsPurchasing(false);
    }
  }, [creditPacks, selectedPackIndex, dispatch]);

  // ── Hero stats ──

  const isTrial = currentUser?.billing_status === "trial";

  const creditsSubtitle = useMemo(() => {
    if (isTrial) {
      // Trial = single fixed budget for the whole trial. No daily reset
      // (the legacy column name still mentions "daily" but the model
      // tracks total trial usage). Show only the trial end date.
      const expiresAt = currentUser?.trial_expires_at;
      if (!expiresAt) return undefined;
      const date = new Date(expiresAt);
      const formatted = date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      return `Trial ends ${formatted}`;
    }
    const resetAt = currentUser?.credits_reset_at;
    if (!resetAt) return undefined;
    const date = new Date(resetAt);
    const formatted = date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return `Resets ${formatted}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const heroStats = useMemo(() => {
    const given = currentUser?.ai_credits_total || 0;
    const used = currentUser?.ai_credits_used || 0;
    const extra = currentUser?.ai_credits_extra || 0;
    return {
      remaining: given - used,
      used,
      total: given,
      extra,
    };
  }, [currentUser?.ai_credits_total, currentUser?.ai_credits_used, currentUser?.ai_credits_extra]);

  // ── Team members (shared credit pool) ──

  const membersList = useMemo(() => {
    return teamUsage.map((m) => ({
      id: m.id,
      name: m.full_name || m.email.split("@")[0],
      email: m.email,
      picture: m.picture,
      credits_used: m.credits_used,
    }));
  }, [teamUsage]);

  // ── Usage history pagination ──

  const handlePagination = (pp: number, requestedPage: number): void => {
    setPerPage(pp);
    setExpandedUsages(new Set([0]));
    fetchUsageHistory(requestedPage, pp);
  };

  // ── Selected pack ──

  const selectedPack = creditPacks[selectedPackIndex] || null;

  // ── Shimmer skeleton helpers ──

  const renderHeroShimmer = () => (
    <div className="ai-credits__hero">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="ai-credits__stat">
          <div className="ai-credits__shimmer ai-credits__shimmer--value" />
          <div className="ai-credits__shimmer ai-credits__shimmer--label" />
        </div>
      ))}
    </div>
  );

  const renderMembersShimmer = () => (
    <div className="ai-credits__members-table">
      <div className="ai-credits__members-header">
        <span className="ai-credits__members-col ai-credits__members-col--member">Member</span>
        <span className="ai-credits__members-col">Used</span>
      </div>
      {[0, 1, 2].map((i) => (
        <div key={i} className="ai-credits__members-row ai-credits__members-row--expanded">
          <div className="ai-credits__members-col ai-credits__members-col--member" data-label="">
            <div className="ai-credits__shimmer ai-credits__shimmer--avatar" />
            <div>
              <div className="ai-credits__shimmer ai-credits__shimmer--name" />
              <div className="ai-credits__shimmer ai-credits__shimmer--email" />
            </div>
          </div>
          <span className="ai-credits__members-col" data-label="Used"><div className="ai-credits__shimmer ai-credits__shimmer--cell" /></span>
        </div>
      ))}
    </div>
  );

  // ── Render ──

  return (
    <Panel
      className="ai-credits"
      title="Credits"
      nodeInTitleRow={creditsSubtitle ? <span className="ai-credits__reset-info">{creditsSubtitle}</span> : null}
    >
      <ByokKeyAlert />
      {/* Hero Stats */}
      {isLoading ? (
        renderHeroShimmer()
      ) : (
        <div className="ai-credits__hero">
          <div className="ai-credits__stat">
            <span className="ai-credits__value ai-credits__value--green">{heroStats.remaining}</span>
            <span className="ai-credits__label">
              <HiOutlineBolt size={16} />
              Credits Remaining
            </span>
          </div>
          <div className="ai-credits__stat">
            <span className="ai-credits__value">{heroStats.used}</span>
            <span className="ai-credits__label">
              <HiOutlineSparkles size={16} />
              Credits Used
            </span>
          </div>
          <div className="ai-credits__stat">
            <span className="ai-credits__value">{heroStats.total}</span>
            <span className="ai-credits__label">
              <HiOutlineCircleStack size={16} />
              Total Credits
            </span>
          </div>
          <div className="ai-credits__stat ai-credits__stat--extra">
            <span className="ai-credits__value ai-credits__value--blue">{heroStats.extra}</span>
            <div className="ai-credits__stat-row">
              <span className="ai-credits__label">
                <HiOutlinePlusCircle size={16} />
                Extra Credits
              </span>
              {canPurchase && hasSubscription ? (
                <button className="ai-credits__topup" onClick={openPurchaseModal}>
                  <HiOutlineShoppingCart size={14} />
                  Top Up
                </button>
              ) : canPurchase && !hasSubscription ? (
                <span className="ai-credits__topup-hint">Subscribe to purchase</span>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Divider */}
      <div className="ai-credits__divider" />

      {/* Team Members */}
      <h3 className="ai-credits__section-title">Team Members</h3>
      {isLoading ? (
        renderMembersShimmer()
      ) : membersList.length > 0 ? (
        <div className="ai-credits__members-table">
          <div className="ai-credits__members-header">
            <span className="ai-credits__members-col ai-credits__members-col--member">
              Member
            </span>
            <span className="ai-credits__members-col">Used</span>
          </div>
          {membersList.map((m, index) => (
            <div
              key={m.id}
              className={`ai-credits__members-row${expandedMembers.has(index) ? " ai-credits__members-row--expanded" : ""}`}
              onClick={(e) => {
                const header = e.currentTarget.firstElementChild;
                if (header && header.contains(e.target as Node)) toggleInSet(setExpandedMembers, index);
              }}
            >
              <div className="ai-credits__members-col ai-credits__members-col--member" data-label="">
                <div className="credits-member">
                  {m.picture ? (
                    <Avatar imageSrc={m.picture} size="small" />
                  ) : (
                    <span className="credits-member__initials">
                      {(m.name || "?")
                        .split(" ")
                        .map((w) => w[0])
                        .join("")
                        .toUpperCase()
                        .slice(0, 2)}
                    </span>
                  )}
                  <div className="credits-member__info">
                    <span className="credits-member__name">{m.name}</span>
                    <span className="credits-member__email">{m.email}</span>
                  </div>
                </div>
              </div>
              <span className="ai-credits__members-col ai-credits__members-col--number" data-label="Used">
                {m.credits_used}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="ai-credits__empty">
          <HiOutlineInboxStack size={32} />
          <span>No member data available</span>
        </div>
      )}

      {/* Divider */}
      <div className="ai-credits__divider" />

      {/* Usage History */}
      <h3 className="ai-credits__section-title">Usage History</h3>
      {usageLoading ? (
        <div className="ai-credits__usage-table">
          <div className="ai-credits__usage-header">
            <span className="ai-credits__usage-col">Date</span>
            <span className="ai-credits__usage-col">User</span>
            <span className="ai-credits__usage-col">Action</span>
            <span className="ai-credits__usage-col ai-credits__usage-col--right">Credits</span>
          </div>
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="ai-credits__usage-row ai-credits__usage-row--expanded">
              <span className="ai-credits__usage-col" data-label="Date"><div className="ai-credits__shimmer ai-credits__shimmer--cell" style={{ width: 100 }} /></span>
              <span className="ai-credits__usage-col" data-label="User"><div className="ai-credits__shimmer ai-credits__shimmer--cell" style={{ width: 80 }} /></span>
              <span className="ai-credits__usage-col" data-label="Action"><div className="ai-credits__shimmer ai-credits__shimmer--cell" style={{ width: 90 }} /></span>
              <span className="ai-credits__usage-col ai-credits__usage-col--right" data-label="Credits"><div className="ai-credits__shimmer ai-credits__shimmer--cell" /></span>
            </div>
          ))}
        </div>
      ) : usageHistory.length > 0 ? (
        <div className="ai-credits__usage-table">
          <div className="ai-credits__usage-header">
            <span className="ai-credits__usage-col">Date</span>
            <span className="ai-credits__usage-col">User</span>
            <span className="ai-credits__usage-col">Action</span>
            <span className="ai-credits__usage-col ai-credits__usage-col--right">Credits</span>
          </div>
          {usageHistory.map((record, index) => (
            <div
              key={record.id}
              className={`ai-credits__usage-row${expandedUsages.has(index) ? " ai-credits__usage-row--expanded" : ""}`}
              onClick={(e) => {
                const header = e.currentTarget.firstElementChild;
                if (header && header.contains(e.target as Node)) toggleInSet(setExpandedUsages, index);
              }}
            >
              <span className="ai-credits__usage-col" data-label="Date">
                {moment(record.created_at).format("DD MMM YYYY, HH:mm")}
              </span>
              <span className="ai-credits__usage-col ai-credits__usage-user" data-label="User">
                {record.user_name}
              </span>
              <span className="ai-credits__usage-col" data-label="Action">
                {(() => {
                  const cfg = ACTION_CONFIG[record.action];
                  const color = cfg?.color || "#929eae";
                  return (
                    <span className="action-inline">
                      <span className="action-inline__icon" style={{ color }}>{cfg?.icon || <HiOutlineSparkles size={14} />}</span>
                      {cfg?.label || record.action}
                    </span>
                  );
                })()}
              </span>
              {record.source === "byok" ? (
                // Generated through the user's own OpenRouter key - no plan credits spent.
                <span className="ai-credits__usage-col ai-credits__usage-col--right" data-label="Credits">
                  <span className="ai-credits__byok-badge">Own AI key</span>
                </span>
              ) : (
                <span className={`ai-credits__usage-col ai-credits__usage-col--right ai-credits__usage-credits${GRANT_ACTIONS.has(record.action) ? " ai-credits__usage-credits--grant" : ""}`} data-label="Credits">
                  {GRANT_ACTIONS.has(record.action) ? "+" : "-"}{record.credits}
                </span>
              )}
            </div>
          ))}
          {usageTotal > perPage && (
            <DataTablePagination
              perPage={perPage}
              perPageOptions={[10, 25, 50]}
              total={usageTotal}
              currentPage={currentPage}
              onPagination={handlePagination}
            />
          )}
        </div>
      ) : (
        <div className="ai-credits__empty">
          <HiOutlineInboxStack size={32} />
          <span>No usage history yet</span>
        </div>
      )}

      {/* ── Purchase Credits Modal ── */}
      <Modal
        open={showPurchaseModal}
        onCancel={() => !isPurchasing && setShowPurchaseModal(false)}
        footer={null}
        centered
        width={520}
        closeIcon={<HiOutlineXMark size={20} />}
        className="purchase-credits-modal"
        destroyOnClose
      >
        <div className="purchase-credits">
          <div className="purchase-credits__icon">
            <HiOutlineBolt size={28} />
          </div>
          <h2 className="purchase-credits__title">Top Up AI Credits</h2>
          <p className="purchase-credits__desc">
            Extra credits never expire and are used after your plan credits run out.
            Requires an active subscription.
          </p>

          {packsLoading ? (
            <div className="purchase-credits__loading">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="purchase-credits__pack-shimmer" />
              ))}
            </div>
          ) : (
            <>
              {/* Slider */}
              <div className="purchase-credits__slider">
                <input
                  type="range"
                  min={0}
                  max={creditPacks.length - 1}
                  value={selectedPackIndex}
                  onChange={(e) => setSelectedPackIndex(Number(e.target.value))}
                  className="purchase-credits__range"
                  style={{ '--progress': `${(selectedPackIndex / Math.max(creditPacks.length - 1, 1)) * 100}%` } as React.CSSProperties}
                />
                <div className="purchase-credits__ticks">
                  {creditPacks.map((pack, i) => (
                    <button
                      key={pack.credits}
                      className={`purchase-credits__tick${i === selectedPackIndex ? " purchase-credits__tick--active" : ""}`}
                      onClick={() => setSelectedPackIndex(i)}
                    >
                      {pack.credits >= 1000 ? `${pack.credits / 1000}k` : pack.credits}
                    </button>
                  ))}
                </div>
              </div>

              {/* Selected pack details */}
              {selectedPack && (
                <div className="purchase-credits__details">
                  <div className="purchase-credits__detail-row purchase-credits__detail-row--main">
                    <span className="purchase-credits__credits-amount">
                      {selectedPack.credits.toLocaleString()} credits
                    </span>
                    <span className="purchase-credits__price">
                      ${selectedPack.price.toFixed(2)}
                    </span>
                  </div>
                  <div className="purchase-credits__detail-row">
                    <span className="purchase-credits__per-credit">
                      ${selectedPack.price_per_credit.toFixed(4)} per credit
                    </span>
                    {selectedPack.discount > 0 && (
                      <span className="purchase-credits__discount">
                        {selectedPack.discount}% off
                      </span>
                    )}
                  </div>
                  {selectedPack.savings > 0 && (
                    <div className="purchase-credits__savings">
                      You save ${selectedPack.savings.toFixed(2)} compared to base price
                    </div>
                  )}
                </div>
              )}

              {/* Purchase button */}
              <FormButton
                theme="green-light"
                className="purchase-credits__buy-btn"
                onClick={handlePurchase}
                disabled={isPurchasing || !selectedPack}
              >
                {isPurchasing
                  ? "Processing..."
                  : selectedPack
                    ? `Buy ${selectedPack.credits.toLocaleString()} Credits for $${selectedPack.price.toFixed(2)}`
                    : "Select a pack"}
              </FormButton>
            </>
          )}
        </div>
      </Modal>
    </Panel>
  );
};

export default Connects;
